import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { trpc } from '../lib/trpc';
import { useTranslation } from '../i18n';
import ShopFooter from '../components/ShopFooter';
import { useAuth } from '../_core/hooks/useAuth';
import { sanitizeHtml } from '../lib/sanitize';

function BackButton() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  return (
    <button
      type='button'
      onClick={() => {
        if (isAuthenticated) {
          window.location.href = '/gallery';
          return;
        }
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = '/';
        }
      }}
      className='inline-flex items-center gap-2 rounded-md border border-muted-foreground/30 px-3 py-1.5 text-sm text-muted-foreground hover:border-muted-foreground hover:text-foreground transition-colors'
    >
      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='h-4 w-4'>
        <path d='M15 18l-6-6 6-6' />
      </svg>
      {t.common.back}
    </button>
  );
}

export default function LegalPage() {
  const { t } = useTranslation();
  const [location] = useLocation();
  const [photographerId, setPhotographerId] = useState<number | null>(null);
  const slug = (location || '/').replace(/^\//,'');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('gallery_session');
      if (raw) {
        const s = JSON.parse(raw);
        if (s.photographerId) setPhotographerId(s.photographerId);
      }
    } catch {}
  }, []);

  const { data: page, isLoading } = trpc.legalPages.getBySlug.useQuery({ photographerId: photographerId || undefined, slug }, { enabled: !!photographerId });

  const contentByLang = (() => {
    if (!page?.content) return null;
    let obj: Record<string, string> | null = null;
    try {
      const parsed = JSON.parse(page.content as unknown as string);
      if (parsed && typeof parsed === 'object') obj = parsed as Record<string, string>;
    } catch {}
    if (!obj) return null;
    // Select content by current language with fallbacks
    const { language } = useTranslation();
    const tryKeys = [language, 'de', 'en', 'hr'];
    for (const k of tryKeys) {
      if (obj[k]) return obj[k];
    }
    // Any available value
    const first = Object.values(obj)[0];
    return first ?? null;
  })();

  return (
    <div className='min-h-screen flex flex-col'>
      <main className='container mx-auto px-4 py-12 flex-1 max-w-3xl'>
        <div className='mb-6'>
          <BackButton />
        </div>
        {isLoading && <p>{t.common.loading}</p>}
        {!isLoading && !page && (
          slug === 'contact' ? (
            <article>
              <h1 className='text-3xl font-bold mb-6'>{t.legal.contactTitle}</h1>
              <div className='prose dark:prose-invert max-w-none'>
                {t.legal.contactDefault}
              </div>
            </article>
          ) : slug === 'impressum' ? (
            <article>
              <h1 className='text-3xl font-bold mb-6'>{t.legal.impressumTitle}</h1>
              <div className='prose dark:prose-invert max-w-none'>
                {t.legal.impressumDefault}
              </div>
            </article>
          ) : (
            <p className='text-muted-foreground'>Seite nicht gefunden oder nicht aktiv.</p>
          )
        )}
        {page && (
          <article>
            <h1 className='text-3xl font-bold mb-6'>{page.title}</h1>
            {contentByLang ? (
              <div className='prose dark:prose-invert max-w-none' dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentByLang) }} />
            ) : (
              <div className='prose dark:prose-invert max-w-none'>
                {/* language-specific fallback if JSON missing */}
                {slug === 'contact' ? t.legal.contactDefault : slug === 'impressum' ? t.legal.impressumDefault : t.common.error}
              </div>
            )}
          </article>
        )}
      </main>
      <ShopFooter />
    </div>
  );
}
